import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Target, CalendarClock, Camera, Loader2, Trophy, Award, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import VestigiaRanking from '@/components/VestigiaRanking';
import { downloadDiploma, shareAchievement } from '@/lib/downloads';

export default function FreeModeProgress({ registrationId, eventId }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rankingOpen, setRankingOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/registrations/${registrationId}/progress`);
      setData(r.data);
    } catch { /* ignore */ }
  }, [registrationId]);

  useEffect(() => { load(); }, [load]);

  const selectChallenge = async (id) => {
    setSaving(true);
    try {
      await axios.put(`${API}/registrations/${registrationId}/challenge`, { challenge_id: id || null });
      await load();
    } catch { toast.error('No se pudo guardar el reto'); } finally { setSaving(false); }
  };

  if (!data) return <div className="p-4 text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando progreso…</div>;

  const sel = data.selected_challenge;
  const daysLeft = data.deadline ? Math.ceil((new Date(data.deadline) - Date.now()) / 86400000) : null;

  return (
    <div className="p-4 bg-violet-50 rounded-lg border border-violet-200 space-y-4" data-testid={`free-progress-${registrationId}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-violet-900 flex items-center gap-1"><Camera className="w-4 h-4" /> Waypoints acreditados</p>
          <p className="text-3xl font-black text-violet-800" data-testid={`free-verified-${registrationId}`}>
            {data.verified}<span className="text-base font-medium text-violet-500"> / {data.total_waypoints}</span>
            {data.pending > 0 && <span className="ml-2 text-sm font-medium text-amber-600">+{data.pending} en revisión</span>}
          </p>
          <p className="text-xs text-violet-700">{data.points.toLocaleString('es-ES')} / {data.points_total.toLocaleString('es-ES')} puntos</p>
        </div>
        {data.deadline && (
          <div className="text-right">
            <p className="text-xs text-gray-500 flex items-center gap-1 justify-end"><CalendarClock className="w-3.5 h-3.5" /> Fecha límite</p>
            <p className="text-sm font-semibold text-gray-800">{data.deadline}</p>
            {daysLeft != null && <p className={`text-xs ${daysLeft < 30 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{daysLeft > 0 ? `quedan ${daysLeft} días` : 'plazo finalizado'}</p>}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {data.by_category.map(c => (
          <span key={c.category} className="inline-flex items-center gap-1 rounded-full pl-1 pr-2 py-0.5 text-xs font-medium bg-white border border-gray-200" data-testid={`free-cat-${c.category}`}>
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || '#9ca3af' }} />
            {c.category} <strong>{c.verified}</strong>/{c.total}{c.pending ? <span className="text-amber-600">+{c.pending}</span> : null}
          </span>
        ))}
      </div>

      <div className="rounded-md bg-white border border-violet-200 p-3 space-y-2">
        <label className="text-sm font-semibold text-gray-900 flex items-center gap-1"><Target className="w-4 h-4 text-orange-500" /> Mi reto</label>
        <select
          value={sel?.id || ''}
          disabled={saving}
          onChange={(e) => selectChallenge(e.target.value)}
          className="w-full h-11 rounded-md border border-gray-300 bg-white px-3 text-sm"
          data-testid={`free-challenge-select-${registrationId}`}
        >
          <option value="">— Elige el reto que persigues —</option>
          {data.challenges.map(ch => <option key={ch.id} value={ch.id}>{ch.name} ({ch.done}/{ch.needed})</option>)}
        </select>
        {sel && (
          <div data-testid={`free-challenge-progress-${registrationId}`}>
            <div className="flex items-baseline justify-between">
              <p className="text-sm text-gray-800">Llevas <strong className="text-emerald-700">{sel.done}</strong> · te faltan <strong className={sel.remaining === 0 ? 'text-emerald-700' : 'text-orange-600'}>{sel.remaining}</strong></p>
              <p className="text-sm font-bold text-gray-700">{sel.pct}%</p>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-100 overflow-hidden mt-1">
              <div className={`h-full rounded-full ${sel.completed ? 'bg-emerald-600' : 'bg-gradient-to-r from-orange-400 to-violet-500'}`} style={{ width: `${sel.pct}%` }} />
            </div>
            {sel.completed && <p className="text-xs font-semibold text-emerald-700 mt-1">¡Reto completado!</p>}
            {sel.rules.length > 1 && (
              <ul className="mt-2 grid grid-cols-2 gap-1 text-xs text-gray-600">
                {sel.rules.map((r, i) => (
                  <li key={i} className={r.remaining === 0 ? 'text-emerald-700' : ''}>{r.category === '*' ? 'Todos' : r.category}: {r.done}/{r.needed}{r.remaining ? ` (faltan ${r.remaining})` : ' ✓'}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {data.challenges.some(c => c.completed) && (
        <div className="space-y-2" data-testid={`free-diplomas-${registrationId}`}>
          {data.challenges.filter(c => c.completed).map(c => (
            <div key={c.id} className="grid grid-cols-[1fr_auto] gap-2">
              {data.diploma_available ? (
                <Button type="button" onClick={() => downloadDiploma(registrationId, c)} className="h-12 bg-amber-500 hover:bg-amber-600 text-white glove-friendly" data-testid={`diploma-btn-${c.id}`}>
                  <Award className="w-5 h-5 mr-2" /> Descargar diploma {c.name}
                </Button>
              ) : <p className="h-12 flex items-center text-sm font-semibold text-emerald-700">¡Reto {c.name} completado!</p>}
              <Button type="button" onClick={() => shareAchievement(registrationId, c)} className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white glove-friendly px-4" title="Compartir logro" data-testid={`share-btn-${c.id}`}>
                <Share2 className="w-5 h-5" /><span className="ml-2 hidden sm:inline">Compartir</span>
              </Button>
            </div>
          ))}
        </div>
      )}

      {eventId && (
        <>
          <Button type="button" variant="outline" onClick={() => setRankingOpen(true)} className="w-full h-11 border-violet-300 text-violet-800 bg-white" data-testid={`ranking-btn-${registrationId}`}>
            <Trophy className="w-4 h-4 mr-2 text-yellow-500" /> Ver clasificación
          </Button>
          <VestigiaRanking eventId={eventId} open={rankingOpen} onOpenChange={setRankingOpen} />
        </>
      )}
    </div>
  );
}
